import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, ShieldCheck, ShieldOff, KeyRound, Copy, Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function TwoFactorModal({ onClose, forceSetup = false }) {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null); // {enabled, backup_codes_remaining, is_admin_mandatory}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Setup state
  const [setupData, setSetupData] = useState(null); // {secret, uri}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  // Disable state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startSetup = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verifySetup = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-setup", { code: code.trim() });
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      // Refrescar user para que requires_2fa_setup se desactive
      try {
        const { data: me } = await api.get("/auth/me");
        setUser({ ...user, ...me, totp_enabled: true, requires_2fa_setup: false });
      } catch { /* noop */ }
      await fetchStatus();
      toast.success("2FA activado correctamente");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        password: disablePassword,
        code: disableCode.trim() || undefined,
      });
      toast.success("2FA desactivado");
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      await fetchStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyBackup = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const canDismiss = true; // siempre permitir cerrar, pero el modal volverá a abrirse en cada login

  return (
    <div
      data-testid="twofa-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={() => { if (canDismiss) onClose(); }}
    >
      <div
        className="w-full max-w-lg panel-strong"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" style={{ color: "var(--cyan)" }} />
            <div>
              <div className="label-mono">// SEGURIDAD</div>
              <h2 className="font-display font-bold text-lg text-slate-100">Autenticación en 2 pasos</h2>
            </div>
          </div>
          {canDismiss && (
            <button data-testid="twofa-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {loading && (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          )}

          {!loading && forceSetup && !status?.enabled && (
            <div className="p-3 rounded-sm flex items-start gap-2"
              style={{ background: "rgba(217,70,239,0.12)", border: "1px solid rgba(217,70,239,0.35)" }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--magenta)" }} />
              <div className="text-xs text-slate-200">
                <b>2FA es obligatorio para administradores.</b> Por seguridad, debes activarlo para continuar usando la app.
              </div>
            </div>
          )}

          {/* Backup codes shown ONCE after successful setup */}
          {backupCodes && (
            <div data-testid="backup-codes" className="space-y-3">
              <div className="p-3 rounded-sm" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.3)" }}>
                <div className="font-bold text-sm text-cyan-300 mb-1">¡2FA activado!</div>
                <div className="text-xs text-slate-300">
                  Guarda estos códigos de respaldo en un lugar seguro. Cada uno funciona <b>UNA sola vez</b> si pierdes tu app autenticadora.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((c, i) => (
                  <div key={i} className="font-mono text-sm py-2 text-center"
                    style={{ background: "rgba(15,23,42,0.6)", border: "1px solid var(--border)" }}>
                    {c}
                  </div>
                ))}
              </div>
              <button
                data-testid="copy-backup-codes"
                onClick={copyBackup}
                className="btn-ghost w-full py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiado" : "Copiar todos"}
              </button>
              <button
                data-testid="backup-codes-acknowledge"
                onClick={onClose}
                className="btn-primary w-full py-2 text-xs uppercase tracking-widest"
              >
                Listo, ya los guardé
              </button>
            </div>
          )}

          {/* Setup flow: QR code + verify */}
          {!backupCodes && setupData && (
            <div data-testid="twofa-setup" className="space-y-4">
              <ol className="text-sm text-slate-300 space-y-2 list-decimal pl-5">
                <li>Descarga <b>Google Authenticator</b>, <b>Authy</b> o <b>Microsoft Authenticator</b> en tu celular.</li>
                <li>Escanea este código QR con la app:</li>
              </ol>
              <div className="flex justify-center p-4 bg-white rounded-sm">
                <QRCodeSVG value={setupData.uri} size={200} level="M" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">O ingrésalo manualmente:</label>
                <div className="font-mono text-xs px-3 py-2 text-cyan-300 break-all"
                  style={{ background: "rgba(15,23,42,0.6)", border: "1px solid var(--border)" }}>
                  {setupData.secret}
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Código de 6 dígitos de la app</label>
                <input
                  type="text"
                  data-testid="twofa-verify-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full input-cyber px-3 py-2.5 text-center tracking-[0.4em] text-xl font-mono"
                  placeholder="123456"
                  autoFocus
                />
              </div>
              {error && <div className="text-sm px-3 py-2 rounded-sm badge-danger">{error}</div>}
              <button
                data-testid="twofa-verify-submit"
                disabled={busy || code.length !== 6}
                onClick={verifySetup}
                className="btn-primary w-full py-2.5 text-xs uppercase tracking-widest disabled:opacity-50"
              >
                {busy ? "Verificando…" : "Activar 2FA"}
              </button>
            </div>
          )}

          {/* Status view */}
          {!loading && !setupData && !backupCodes && status && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-sm"
                style={{ background: status.enabled ? "rgba(22,163,74,0.1)" : "rgba(100,116,139,0.1)", border: `1px solid ${status.enabled ? "rgba(22,163,74,0.35)" : "var(--border)"}` }}>
                {status.enabled ? (
                  <ShieldCheck className="w-6 h-6" style={{ color: "#22c55e" }} />
                ) : (
                  <ShieldOff className="w-6 h-6 text-slate-400" />
                )}
                <div>
                  <div className="font-semibold text-sm text-slate-100">
                    {status.enabled ? "2FA está activo" : "2FA no está activado"}
                  </div>
                  {status.enabled && (
                    <div className="text-[11px] text-slate-400">
                      Códigos de respaldo restantes: <b className="text-slate-200">{status.backup_codes_remaining}</b>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-xs text-slate-400 leading-relaxed">
                {status.enabled
                  ? "Al iniciar sesión, además de tu contraseña te pediremos un código de 6 dígitos de tu app autenticadora."
                  : status.is_admin_mandatory
                    ? "Como administrador, 2FA es OBLIGATORIO. Una vez activado, no podrás desactivarlo."
                    : "Añade una capa extra de seguridad usando Google Authenticator, Authy o cualquier app TOTP."
                }
              </div>

              {error && <div className="text-sm px-3 py-2 rounded-sm badge-danger">{error}</div>}

              {!status.enabled ? (
                <button
                  data-testid="twofa-start-setup"
                  onClick={startSetup}
                  disabled={busy}
                  className="btn-primary w-full py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Configurar 2FA
                </button>
              ) : (
                !status.is_admin_mandatory && (
                  showDisable ? (
                    <div className="space-y-2 p-3 rounded-sm" style={{ border: "1px dashed rgba(220,38,38,0.4)" }}>
                      <div className="text-xs text-slate-300 mb-2">
                        Para desactivar, ingresa tu contraseña y un código (TOTP o respaldo).
                      </div>
                      <input
                        type="password"
                        data-testid="twofa-disable-password"
                        placeholder="Contraseña actual"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        className="w-full input-cyber px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        data-testid="twofa-disable-code"
                        placeholder="Código 6 dígitos o backup"
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value.toUpperCase())}
                        className="w-full input-cyber px-3 py-2 text-sm font-mono"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowDisable(false); setDisablePassword(""); setDisableCode(""); }}
                          className="btn-ghost flex-1 py-2 text-xs uppercase tracking-widest"
                        >
                          Cancelar
                        </button>
                        <button
                          data-testid="twofa-disable-confirm"
                          onClick={disable}
                          disabled={busy || !disablePassword || !disableCode}
                          className="btn-ghost flex-1 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
                          style={{ borderColor: "#dc2626", color: "#fca5a5" }}
                        >
                          {busy ? "..." : "Desactivar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid="twofa-disable-btn"
                      onClick={() => setShowDisable(true)}
                      className="btn-ghost w-full py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                      style={{ borderColor: "#dc2626", color: "#fca5a5" }}
                    >
                      <ShieldOff className="w-4 h-4" /> Desactivar 2FA
                    </button>
                  )
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
